import React, { useEffect, useState } from 'react';
import { useFinance } from '../context/FinanceContext';
import { TrendingUp, TrendingDown, DollarSign, Brain, AlertCircle, PieChart, CreditCard, ChevronLeft, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function Reports() {
  const { reportsData, reportsLoading, fetchReports } = useFinance();
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  if (reportsLoading) {
    return (
      <div className="flex justify-center items-center h-full min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!reportsData) {
    return (
      <div className="p-4 text-center text-gray-500 mt-10">
        Failed to load reports data.
      </div>
    );
  }

  const { averages, savingsRate, topCategories, debtStatus, advice, monthlyBreakdown, monthCount } = reportsData;

  return (
    <div className="max-w-7xl mx-auto pb-24 md:pb-6 space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col gap-4 sticky top-0 bg-gray-50 z-20 py-4 px-4 sm:px-6 lg:px-8 shadow-sm">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-gray-500 hover:text-gray-800 transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Intelligent Reports</h1>
            <p className="text-sm text-gray-500">Based on your {monthCount} {monthCount === 1 ? 'month' : 'months'} of history</p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-2 mt-2 bg-gray-200 p-1 rounded-lg self-start">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'overview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('breakdown')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'breakdown' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-300'
            }`}
          >
            Monthly Breakdown
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 space-y-6">
        
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Intelligent Advice Section */}
            <section className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-2xl shadow-sm border border-indigo-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Brain className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-lg font-bold text-indigo-900">Katabo AI Advice</h2>
              </div>
              <div className="space-y-3">
                {advice.map((str, idx) => (
                  <div key={idx} className="flex gap-3 items-start bg-white/60 p-4 rounded-xl">
                    <AlertCircle className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                    <p className="text-indigo-900 text-sm">{str}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Key Metrics */}
            <section>
              <h3 className="text-lg font-bold text-gray-800 mb-4 px-1">Key Metrics</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-green-50 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">Avg Income</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">K{averages.income}</p>
                </div>
                
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-red-50 rounded-lg">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">Avg Spend</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">K{averages.spend}</p>
                </div>

                <div className="col-span-2 md:col-span-1 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-blue-50 rounded-lg">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">Savings Rate</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-gray-900">{savingsRate}%</p>
                    <span className="text-sm text-gray-500 mb-1">per month</span>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Categories */}
              <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-6">
                  <PieChart className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-bold text-gray-800">Top Spending Areas</h3>
                </div>
                
                {topCategories.length === 0 ? (
                  <p className="text-sm text-gray-500">Not enough data to calculate categories.</p>
                ) : (
                  <div className="space-y-5">
                    {topCategories.map((cat, idx) => {
                      const maxAmount = topCategories[0].amount;
                      const percent = (cat.amount / maxAmount) * 100;
                      return (
                        <div key={idx}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700 uppercase">{cat.name}</span>
                            <span className="font-bold text-gray-900">K{cat.amount}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ width: `${percent}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Debt Overview */}
              <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-bold text-gray-800">Debt Overview</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex justify-between items-center">
                      <span className="text-sm text-gray-600 font-medium">Unpaid Debts</span>
                      <span className="text-lg font-bold text-red-600">K{debtStatus.unpaid}</span>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex justify-between items-center">
                      <span className="text-sm text-gray-600 font-medium">Paid Debts</span>
                      <span className="text-lg font-bold text-green-600">K{debtStatus.paid}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 text-sm text-gray-500 bg-blue-50 p-4 rounded-xl text-center">
                  Total historical debt recorded: K{debtStatus.paid + debtStatus.unpaid}
                </div>
              </section>
            </div>
          </div>
        )}

        {activeTab === 'breakdown' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="p-4 text-sm font-semibold text-gray-600">Month</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Income</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Spend</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Savings Rate</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Recommendation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {monthlyBreakdown.map((m) => (
                    <tr 
                      key={m.id} 
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/month/${m.id}`)}
                    >
                      <td className="p-4">
                        <div className="font-medium text-gray-900">{m.month}</div>
                        <div className="text-xs text-gray-500">{m.year}</div>
                      </td>
                      <td className="p-4 font-medium text-green-600">K{m.income}</td>
                      <td className="p-4 font-medium text-gray-900">K{m.spend}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          m.savingsRate >= 10 ? 'bg-green-100 text-green-800' :
                          m.savingsRate > 0 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {m.savingsRate}%
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-600 max-w-xs">{m.recommendation}</td>
                    </tr>
                  ))}
                  {monthlyBreakdown.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">No historical data found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {monthlyBreakdown.map((m) => (
                <div 
                  key={m.id} 
                  className="p-4 active:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/month/${m.id}`)}
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{m.month} {m.year}</div>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      m.savingsRate >= 10 ? 'bg-green-100 text-green-800' :
                      m.savingsRate > 0 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {m.savingsRate}% saved
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm mb-3">
                    <div>
                      <span className="text-gray-500 block text-xs">Income</span>
                      <span className="font-medium text-green-600">K{m.income}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-500 block text-xs">Spend</span>
                      <span className="font-medium text-gray-900">K{m.spend}</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded-lg flex gap-2 items-start">
                    <AlertCircle className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-indigo-900">{m.recommendation}</p>
                  </div>
                </div>
              ))}
              {monthlyBreakdown.length === 0 && (
                <div className="p-8 text-center text-gray-500">No historical data found.</div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
